/**
 * Spec 037: an agent's journal of lessons — what it got wrong and how it is right. Its head,
 * people and the agent itself write them; people delete the wrong ones. Twenty at most are
 * active; a new instruction takes them in and they go to the archive.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Lessons, type LessonOut } from "@/api/units";
import { Button } from "@/components/ui/Button";
import { TextArea } from "@/components/ui/Field";
import { SkeletonList } from "@/components/ui/Skeleton";
import { useToast } from "@/components/ui/Toast";
import { ago, Caption, EmptyNote } from "@/kit";
import { LESSON_LIMIT, lessonBy, lessonState } from "@/lib/lessons";

function LessonRow({ lesson, onDelete, busy }: { lesson: LessonOut; onDelete?: () => void; busy?: boolean }) {
  return (
    <li className="space-y-1 border-b border-p-border px-4 py-3 last:border-0">
      <p className="whitespace-pre-wrap break-words text-body text-p-text">{lesson.text_md}</p>
      <p className="text-meta text-p-text-muted">
        {[lessonBy(lesson), lesson.order_number ? `поручение №${lesson.order_number}` : null, lessonState(lesson), ago(lesson.created_at)]
          .filter(Boolean)
          .join(" · ")}
      </p>
      {onDelete && (
        <button type="button" className="min-h-11 text-meta font-semibold text-p-link" disabled={busy} onClick={onDelete}>
          Удалить урок
        </button>
      )}
    </li>
  );
}

export function AgentLessons({ agentId }: { agentId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [text, setText] = useState("");
  const active = useQuery({ queryKey: ["lessons", agentId, "active"], queryFn: () => Lessons.of(agentId, "active") });
  const archived = useQuery({ queryKey: ["lessons", agentId, "archived"], queryFn: () => Lessons.of(agentId, "archived") });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["lessons", agentId] });
    void qc.invalidateQueries({ queryKey: ["agents"] });
    void qc.invalidateQueries({ queryKey: ["departments"] });
  };
  const write = useMutation({
    mutationFn: () => Lessons.write(agentId, text.trim()),
    onSuccess: () => {
      setText("");
      toast.success("Урок записан: агент получит его со следующим шагом");
      refresh();
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const remove = useMutation({
    mutationFn: (id: string) => Lessons.remove(id),
    onSuccess: () => {
      toast.success("Урок удалён");
      refresh();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (active.isLoading) return <SkeletonList count={2} />;
  const rows = active.data ?? [];
  const full = rows.length >= LESSON_LIMIT;
  return (
    <div className="flex flex-col gap-3">
      <section className="space-y-1.5">
        <Caption>Действующие уроки · {rows.length} из {LESSON_LIMIT}</Caption>
        {rows.length ? (
          <ul className="overflow-hidden rounded-card border border-p-border bg-p-surface">
            {rows.map((l) => (
              <LessonRow key={l.id} lesson={l} busy={remove.isPending} onDelete={() => remove.mutate(l.id)} />
            ))}
          </ul>
        ) : (
          <EmptyNote>Уроков нет. Урок — что агент сделал не так и как правильно; он работает по ним сразу, без новой версии.</EmptyNote>
        )}
      </section>
      <section className="space-y-2">
        <TextArea label="Новый урок" hint="Что было не так и как правильно — до 500 знаков" value={text} rows={3} maxLength={500}
          onChange={(e) => setText(e.target.value)} />
        {full && <p className="text-meta text-p-needs">Журнал полон: сначала новая инструкция — она заберёт уроки в архив.</p>}
        <div>
          <Button size="compact" variant="strong" disabled={!text.trim() || full} loading={write.isPending} onClick={() => write.mutate()}>
            Записать урок
          </Button>
        </div>
      </section>
      {(archived.data ?? []).length > 0 && (
        <section className="space-y-1.5">
          <Caption>Вписаны в инструкцию</Caption>
          <ul className="overflow-hidden rounded-card border border-p-border bg-p-surface">
            {(archived.data ?? []).map((l) => <LessonRow key={l.id} lesson={l} />)}
          </ul>
        </section>
      )}
    </div>
  );
}
